'use client';

import React, { useEffect, useState } from 'react';
import styles from './ZoomMeetingDetails.module.css';
import SessionRegistrationForm from './SessionRegistrationForm';

export interface SessionData {
  id: string;
  title: string;
  subtitle?: string;
  summary: string;
  dialog_subtitle?: string;
  meeting_date: string;
  meeting_id: string;
  passcode: string;
  join_url: string;
  ai_companion_url?: string;
  join_instructions_url?: string;
  dial_in_numbers?: Array<{ number: string; location: string }>;
  is_recurring?: boolean;
  status: string;
  agenda_pdf_url?: string;
}

interface ZoomMeetingDetailsProps {
  sessionData?: SessionData;
  onClose?: () => void;
  showCloseButton?: boolean;
  requireRegistration?: boolean;
}

const ZoomMeetingDetails: React.FC<ZoomMeetingDetailsProps> = ({
  sessionData,
  onClose,
  showCloseButton = true,
  requireRegistration = true,
}) => {

  const [hasRegistered, setHasRegistered] = useState(false);

  const storageKey = sessionData ? `session-registered:${sessionData.id}` : null;

  useEffect(() => {
    if (!storageKey) return;
    try {
      const value = window.localStorage.getItem(storageKey);
      if (value === 'true') {
        setHasRegistered(true);
      }
    } catch {
      // Ignore storage errors (e.g., disabled cookies)
    }
  }, [storageKey]);

  const handleRegistered = (data: { name: string; email: string }) => {
    setHasRegistered(true);
    if (storageKey) {
      try {
        window.localStorage.setItem(storageKey, 'true');
      } catch {
        // Ignore storage errors
      }
    }
  };

  // Use dynamic session data or fall back to hardcoded data for backward compatibility
  const meeting = sessionData ? {
    topic: sessionData.title + (sessionData.subtitle ? `: ${sessionData.subtitle}` : ''),
    date: new Date(sessionData.meeting_date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    }),
    time: new Date(sessionData.meeting_date).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      timeZoneName: 'short'
    }),
    joinUrl: sessionData.join_url,
    meetingId: sessionData.meeting_id,
    passcode: sessionData.passcode,
    dialIn: sessionData.dial_in_numbers || [],
    aiCompanionUrl: sessionData.ai_companion_url,
    joinInstructionsUrl: sessionData.join_instructions_url,
    dialogSubtitle: sessionData.dialog_subtitle || sessionData.summary
  } : {
    topic: 'The Power of Prioritization: TimeLee Cares Foundation Overview + A Session to Fuel Your Focus',
    date: 'Nov 16, 2025',
    time: '4:00 PM Eastern Time (US and Canada)',
    joinUrl: 'https://us06web.zoom.us/j/84571833661?pwd=tH3H7h8ebbnjfsaZObNYKj5G3LiMLs.1',
    meetingId: '845 7183 3661',
    passcode: '163310',
    dialIn: [
      { number: '+16469313860,,84571833661#,,,,*163310#', location: 'US' },
      { number: '+13017158592,,84571833661#,,,,*163310#', location: 'US (Washington DC)' }
    ],
    aiCompanionUrl: 'https://us06web.zoom.us/launch/edl?muid=d356706d-8d19-4041-a86c-e530548973d0',
    joinInstructionsUrl: 'https://us06web.zoom.us/meetings/84571833661/invitations?signature=88egvYUICS1G9qK6RXSgyTDm5BmqenBigV1VUcDwbRQ',
    dialogSubtitle: 'Don\'t miss this opportunity to learn powerful prioritization techniques for your academic and career success.'
  };

  // If registration is required and we have a real session, show a small registration
  // form before revealing full Zoom details. Fallback (no sessionData) skips gating.
  if (requireRegistration && sessionData && !hasRegistered) {
    return (
      <div className={styles.successContainer}>
        <div className={styles.content}>
          <div className={styles.header}>
            <h2>Register to Join This Session 📅</h2>
            <p>{meeting.dialogSubtitle}</p>
          </div>

          <div className={styles.meetingInfo}>
            <div className={styles.meetingDetails}>
              <div className={styles.detailItem}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <polyline points="12 6 12 12 16 14"></polyline>
                </svg>
                <div>
                  <div className={styles.detailLabel}>Date & Time</div>
                  <div>{meeting.date} • {meeting.time}</div>
                </div>
              </div>
            </div>

            <div style={{ marginTop: '1.5rem' }}>
              <p style={{ marginBottom: '0.75rem', color: 'var(--text-muted)' }}>
                Please enter your name and email to register for this session. We’ll send you
                a confirmation email with the Zoom link and reminders before the session.
              </p>
              <SessionRegistrationForm
                zoomSessionId={sessionData.id}
                sessionTitle={sessionData.title}
                onRegistered={handleRegistered}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.successContainer}>
      <div className={styles.content}>
        <div className={styles.header}>
          <h2>Join Our Upcoming Session! 📅</h2>
          <p>{meeting.dialogSubtitle}</p>
        </div>

        <div className={styles.meetingInfo}>
          <a
            href={meeting.joinUrl}
            className={styles.zoomButton}
            target="_blank"
            rel="noopener noreferrer"
          >
            Join Zoom Meeting
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
              <polyline points="15 3 21 3 21 9"></polyline>
              <line x1="10" y1="14" x2="21" y2="3"></line>
            </svg>
          </a>

          <div className={styles.meetingDetails}>
            <div className={styles.detailItem}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <polyline points="12 6 12 12 16 14"></polyline>
              </svg>
              <div>
                <div className={styles.detailLabel}>Date & Time</div>
                <div>{meeting.date} • {meeting.time}</div>
              </div>
            </div>

            <div className={styles.detailItem}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M23 7l-7 5 7 5V7z"></path>
                <rect x="1" y="5" width="15" height="14" rx="2" ry="2"></rect>
              </svg>
              <div>
                <div className={styles.detailLabel}>Meeting ID</div>
                <div>{meeting.meetingId} (Passcode: {meeting.passcode})</div>
              </div>
            </div>

            {meeting.dialIn && meeting.dialIn.length > 0 && (
              <div className={styles.detailItem}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                </svg>
                <div>
                  <div className={styles.detailLabel}>US Phone Number</div>
                  {meeting.dialIn.filter(item => item.location === 'US').map((item, index) => {
                    const phoneNumber = item.number.split(',,')[0];
                    return (
                      <div key={index}>
                        <a href={`tel:${phoneNumber.replace(/\D/g, '')}`} style={{color: 'var(--primary-color)', textDecoration: 'none'}}>
                          {phoneNumber}
                        </a> (Then enter: {meeting.meetingId}# and passcode: {meeting.passcode}#)
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ZoomMeetingDetails;
